import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Checkbox, notification } from "antd";
import TenantsApi from "../../common/Apis/TenantApi";

const UpdateTenantModal = ({
  visible,
  onCancel,
  onOk,
  initialValues,
  handleFieldsChange,
}) => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);

  // Load initial values when modal opens
  useEffect(() => {
    if (visible) {
      form.setFieldsValue(initialValues || {});
      validateForm(initialValues || {});
    }
  }, [visible, initialValues]);

  // Custom validation logic for required fields
  const validateForm = (values) => {
    const tenantName = values.tenantName?.trim();
    const contactPerson = values.contactPerson?.trim();
    const contactAddress = values.contactAddress?.trim();
    const contactEmail = values.contactEmail?.trim();
    const contactPhone = values.contactPhone?.trim();

    const hasRequired =
      tenantName &&
      contactPerson &&
      contactAddress &&
      (contactEmail || contactPhone);

    // Check email/phone format
    let validEmail = true;
    let validPhone = true;

    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      validEmail = false;
    }
    if (contactPhone && !/^[0-9+\-\s()]+$/.test(contactPhone)) {
      validPhone = false;
    }

    setIsFormValid(hasRequired && validEmail && validPhone);
  };

  const handleValuesChange = (_, allValues) => {
    validateForm(allValues);

    if (handleFieldsChange) {
      handleFieldsChange(_, allValues);
    }
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = {
          ...initialValues,
          tenantCode: values.tenantCode,
          tenantName: values.tenantName,
          multitenancy: values.multitenancy || false,
          contactPerson: values.contactPerson,
          contactEmail: values.contactEmail,
          contactPhone: values.contactPhone,
          contactAddress: values.contactAddress,
        };

        TenantsApi.Edittenants(initialValues?.id, payload)
          .then(() => {
            notification.open({
              closeIcon: false,
              message: "Tenant / Customer updated successfully!",
              duration: 3,
              style: { backgroundColor: "#b3ffb3", color: "green" },
            });
            form.resetFields();
            onOk(payload);
            onCancel();
          })
          .catch((error) => {
            notification.error({
              message: "Failed to update tenant",
              description:
                error?.response?.data?.message || "An unexpected error occurred.",
              duration: 4,
            });
          });
      })
      .catch((info) => {
        console.log("Validation failed:", info);
      });
  };

  return (
    <Modal
      title={<div style={{ textAlign: "center", width: "100%" }}>Update Tenant / Customer</div>}
      open={visible}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={handleOk}
      okText="Update"
      cancelText="Cancel"
      maskClosable={false}
      okButtonProps={{ disabled: !isFormValid }}
    >
      <Form
        form={form}
        layout="vertical"
        name="update-tenant-form"
        onValuesChange={handleValuesChange}
      >
        <Form.Item
          name="tenantCode"
          label={<span>Tenant Code <span style={{ color: "red" }}>*</span></span>}
          rules={[
            { required: true, message: "Tenant Code is required" },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (/\s/.test(value)) {
                  return Promise.reject(
                    new Error("Tenant Code should not contain spaces")
                  );
                }
                if (!/^[a-zA-Z0-9_-]*$/.test(value)) {
                  return Promise.reject(
                    new Error(
                      "Only letters, numbers, hyphens, and underscores are allowed"
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="Enter tenant code" disabled />
        </Form.Item>

        <Form.Item
          name="tenantName"
          label={<span>Tenant Name <span style={{ color: "red" }}>*</span></span>}
          rules={[{ required: true, message: "Tenant Name is required" }]}
        >
          <Input placeholder="Enter tenant name" />
        </Form.Item>

        <Form.Item
          name="contactPerson"
          label={<span>Contact Person <span style={{ color: "red" }}>*</span></span>}
          rules={[{ required: true, message: "Contact person is required" }]}
        >
          <Input placeholder="Enter contact person" />
        </Form.Item>

        <Form.Item
          name="contactEmail"
          label="Contact Email"
        >
          <Input placeholder="Enter contact email" />
        </Form.Item>

        <Form.Item
          name="contactPhone"
          label="Contact Phone"
        >
          <Input placeholder="Enter contact phone" />
        </Form.Item>

        <Form.Item
          name="contactAddress"
          label={<span>Contact Address <span style={{ color: "red" }}>*</span></span>}
          rules={[{ required: true, message: "Contact address is required" }]}
        >
          <Input.TextArea placeholder="Enter contact address" />
        </Form.Item>

        <div className="d-flex align-items-center">
          <label className="me-2">Is this Tenant / Customer has multiple stores?</label>
          <Form.Item name="multitenancy" valuePropName="checked" noStyle>
            <Checkbox />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default UpdateTenantModal;
